import {
  FormEvent,
  MouseEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  approveSafetyCheckSheet,
  createSafetyCheckSheet,
  deleteSafetyCheckSheet,
  deleteSafetyCheckSheetAttachment,
  downloadSafetyCheckSheetAttachment,
  fetchSafetyCheckSheet,
  fetchSafetyCheckSheets,
  revokeSafetyCheckSheet,
  updateSafetyCheckSheet,
  uploadSafetyCheckSheetAttachments,
  downloadSafetyCheckSheetPdf,
} from "../../api/safety";
import { useCompanyStore } from "../../store/companyStore";
import { APPROVAL_TARGET_SAFETY_CHECK } from "../../types/approval";
import { canConfirmInspection } from "../../types/companyRole";
import {
  createEmptySafetyCheckItem,
  defaultSafetyCheckItems,
  parseSafetyCheckItems,
  SafetyCheckItem,
  SafetyCheckSheet,
  SafetyCheckSheetAttachment,
  safetyStatusLabel,
} from "../../types/safety";
import { notifyError, reportError } from "../../utils/notify";
import {
  ApprovalPendingFields,
  isApprovalInProgress,
  withApprovalPendingClass,
  withApprovalPendingLabel,
} from "../../utils/approvalPending";
import ApprovalCancelButton from "../approval/ApprovalCancelButton";
import ApprovalRequestModal from "../approval/ApprovalRequestModal";

import "../quality/EducationAnnualPlanTab.css";
import "../quality/QualityShell.css";
import "../product/InspectionFormSheet.css";
import "../product/NonconformanceReportPanel.css";
import "../quality/CleanActivityPanel.css";
import "../quality/SmallGroupPanel.css";

function todayIso() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function formatDate(value?: string | null) {
  if (!value) return "-";
  return value.slice(0, 10);
}

function formatSize(size?: number | null) {
  if (size == null) return "";
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

function apiErrorMessage(err: unknown, fallback: string) {
  const message = (err as { response?: { data?: { message?: string } } })
    ?.response?.data?.message;
  return message?.trim() || fallback;
}

function isApproved(status?: string | null) {
  return (status || "").toUpperCase() === "APPROVED";
}

function isDraft(status?: string | null) {
  const s = (status || "DRAFT").toUpperCase();
  return s === "DRAFT" || s === "";
}

function StampBox({
  authorName,
  reviewerName,
  approverName,
}: {
  authorName?: string | null;
  reviewerName?: string | null;
  approverName?: string | null;
}) {
  return (
    <div className="ifs-sheet__stamps">
      {(
        [
          ["작성", authorName],
          ["검토", reviewerName],
          ["승인", approverName],
        ] as const
      ).map(([label, value]) => (
        <div key={label}>
          <strong>{label}</strong>
          <span
            style={{
              color: value ? "#1f2a37" : "#98a2b3",
              fontWeight: value ? 600 : 400,
            }}
          >
            {value || "-"}
          </span>
        </div>
      ))}
    </div>
  );
}

type DocView =
  | { type: "list" }
  | { type: "create" }
  | { type: "edit"; sheetId: number }
  | { type: "detail"; sheetId: number };

const LIST_VIEW = { type: "list" } as const;

function SafetyCheckSheetPanel({
  contractId = null,
  embedded = false,
}: {
  contractId?: number | null;
  embedded?: boolean;
} = {}) {
  void embedded;
  const companyId = useCompanyStore((s) => s.companyId);
  const role = useCompanyStore((s) => s.role);
  const canApproveDirect = canConfirmInspection(role);
  const fileRef = useRef<HTMLInputElement>(null);

  const [view, setView] = useState<DocView>(LIST_VIEW);
  const [rows, setRows] = useState<SafetyCheckSheet[]>([]);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [approving, setApproving] = useState(false);
  const [pdfDownloading, setPdfDownloading] = useState(false);
  const [error, setError] = useState("");
  const [approvalMessage, setApprovalMessage] = useState("");
  const [approvalOpen, setApprovalOpen] = useState(false);

  const [sheetNo, setSheetNo] = useState("");
  const [title, setTitle] = useState("");
  const [checkDate, setCheckDate] = useState(todayIso());
  const [areaName, setAreaName] = useState("");
  const [checkerName, setCheckerName] = useState("");
  const [remark, setRemark] = useState("");
  const [status, setStatus] = useState("DRAFT");
  const [items, setItems] = useState<SafetyCheckItem[]>(
    defaultSafetyCheckItems(),
  );
  const [authorName, setAuthorName] = useState<string | null>(null);
  const [reviewerName, setReviewerName] = useState<string | null>(null);
  const [approverName, setApproverName] = useState<string | null>(null);
  const [canRevoke, setCanRevoke] = useState(false);
  const [approvalPending, setApprovalPending] = useState<ApprovalPendingFields>(
    {},
  );
  const [attachments, setAttachments] = useState<SafetyCheckSheetAttachment[]>(
    [],
  );
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);

  const editId =
    view.type === "edit" || view.type === "detail" ? view.sheetId : null;
  const showForm = view.type !== "list";
  const isDetail = view.type === "detail";
  const isEditing = view.type === "create" || view.type === "edit";
  const locked = !isEditing;

  const attachmentsLocked =
    isApproved(status) ||
    isApprovalInProgress(approvalPending) ||
    (!isEditing && !isDraft(status));

  const applyDetail = (detail: SafetyCheckSheet) => {
    setSheetNo(detail.sheetNo || "");
    setTitle(detail.title || "");
    setCheckDate(detail.checkDate ? detail.checkDate.slice(0, 10) : todayIso());
    setAreaName(detail.areaName || "");
    setCheckerName(detail.checkerName || "");
    setRemark(detail.remark || "");
    setStatus(detail.status || "DRAFT");
    setItems(parseSafetyCheckItems(detail.itemsJson));
    setAuthorName(detail.authorName ?? null);
    setReviewerName(detail.reviewerName ?? null);
    setApproverName(detail.approverName ?? null);
    setCanRevoke(detail.canRevoke ?? false);
    setApprovalPending({
      approvalInProgress: detail.approvalInProgress,
      pendingApprovalRequestId: detail.pendingApprovalRequestId,
      canCancelApproval: detail.canCancelApproval,
    });
    setAttachments(detail.attachments || []);
  };

  const loadList = useCallback(
    async (keyword?: string) => {
      try {
        setLoading(true);
        setRows(await fetchSafetyCheckSheets(keyword, contractId));
        setError("");
      } catch (err: unknown) {
        reportError(
          setError,
          apiErrorMessage(err, "안전관리 점검시트 목록을 불러오지 못했습니다."),
        );
        setRows([]);
      } finally {
        setLoading(false);
      }
    },
    [contractId],
  );

  useEffect(() => {
    setView(LIST_VIEW);
    setSearchKeyword("");
    void loadList();
  }, [companyId, loadList, setView]);

  useEffect(() => {
    if (showForm) return;
    const timer = window.setTimeout(() => void loadList(searchKeyword), 250);
    return () => window.clearTimeout(timer);
  }, [searchKeyword, loadList, showForm]);

  const resetForm = () => {
    setSheetNo("");
    setTitle("안전관리 점검시트");
    setCheckDate(todayIso());
    setAreaName("");
    setCheckerName("");
    setRemark("");
    setStatus("DRAFT");
    setItems(defaultSafetyCheckItems());
    setAuthorName(null);
    setReviewerName(null);
    setApproverName(null);
    setCanRevoke(false);
    setApprovalPending({});
    setAttachments([]);
    setPendingFiles([]);
    setApprovalMessage("");
    setApprovalOpen(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const openCreate = () => {
    resetForm();
    setView({ type: "create" });
  };

  const openDetail = async (row: SafetyCheckSheet) => {
    try {
      setLoading(true);
      setError("");
      const detail = await fetchSafetyCheckSheet(row.sheetId);
      applyDetail(detail);
      setPendingFiles([]);
      if (fileRef.current) fileRef.current.value = "";
      setView({ type: "detail", sheetId: row.sheetId });
    } catch (err: unknown) {
      reportError(
        setError,
        apiErrorMessage(err, "안전관리 점검시트를 불러오지 못했습니다."),
      );
    } finally {
      setLoading(false);
    }
  };

  const openEditFromDetail = () => {
    if (editId == null || isApproved(status)) return;
    setView({ type: "edit", sheetId: editId });
  };

  const backToList = () => {
    resetForm();
    setView(LIST_VIEW);
    void loadList(searchKeyword);
  };

  const patchItem = (idx: number, patch: Partial<SafetyCheckItem>) => {
    setItems((prev) =>
      prev.map((row, i) => (i === idx ? { ...row, ...patch } : row)),
    );
  };

  const handleSave = async (event: FormEvent) => {
    event.preventDefault();
    if (locked) {
      reportError(setError, "승인된 점검시트는 수정할 수 없습니다.");
      return;
    }
    if (!title.trim()) {
      reportError(setError, "제목을 입력해 주세요.");
      return;
    }
    try {
      setSaving(true);
      setError("");
      const payload = {
        title: title.trim(),
        checkDate: checkDate || null,
        areaName: areaName.trim() || null,
        checkerName: checkerName.trim() || null,
        itemsJson: JSON.stringify(items),
        remark: remark.trim() || null,
        contractId: contractId ?? undefined,
      };
      let sheetId = editId;
      let saved: SafetyCheckSheet;
      if (sheetId != null) {
        saved = await updateSafetyCheckSheet(sheetId, payload);
      } else {
        saved = await createSafetyCheckSheet(payload);
        sheetId = saved.sheetId;
      }
      if (pendingFiles.length > 0 && sheetId != null) {
        const uploaded = await uploadSafetyCheckSheetAttachments(
          sheetId,
          pendingFiles,
        );
        saved = {
          ...saved,
          attachments: [...(saved.attachments || []), ...uploaded],
          attachmentCount: (saved.attachmentCount ?? 0) + uploaded.length,
        };
        setPendingFiles([]);
      }
      applyDetail(saved);
      setPendingFiles([]);
      setView({ type: "detail", sheetId: saved.sheetId });
      await loadList(searchKeyword);
    } catch (err: unknown) {
      reportError(
        setError,
        apiErrorMessage(err, "안전관리 점검시트 저장에 실패했습니다."),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteFromList = async (
    row: SafetyCheckSheet,
    event: MouseEvent,
  ) => {
    event.stopPropagation();
    if (isApproved(row.status)) {
      reportError(setError, "승인된 점검시트는 삭제할 수 없습니다.");
      return;
    }
    if (
      !window.confirm(
        `"${row.title || row.sheetNo || "점검시트"}"를 삭제할까요?`,
      )
    )
      return;
    try {
      await deleteSafetyCheckSheet(row.sheetId);
      await loadList(searchKeyword);
    } catch (err: unknown) {
      reportError(
        setError,
        apiErrorMessage(err, "안전관리 점검시트 삭제에 실패했습니다."),
      );
    }
  };

  const handleApprove = async () => {
    if (editId == null) return;
    if (!canApproveDirect) {
      reportError(setError, "바로 승인은 오너/팀장만 가능합니다.");
      return;
    }
    if (
      !window.confirm(
        "점검시트를 바로 승인할까요? 승인 후에는 수정·삭제할 수 없습니다.",
      )
    ) {
      return;
    }
    try {
      setApproving(true);
      setError("");
      const saved = await approveSafetyCheckSheet(editId);
      applyDetail(saved);
      setApprovalMessage("바로 승인되었습니다.");
      await loadList(searchKeyword);
    } catch (err: unknown) {
      reportError(setError, apiErrorMessage(err, "바로 승인에 실패했습니다."));
    } finally {
      setApproving(false);
    }
  };

  const handleRevoke = async () => {
    if (editId == null) return;
    if (!window.confirm("승인을 철회할까요?\n승인란·검토란이 지워집니다."))
      return;
    try {
      setApproving(true);
      setError("");
      const saved = await revokeSafetyCheckSheet(editId);
      applyDetail(saved);
      setApprovalMessage("승인이 철회되었습니다.");
      await loadList(searchKeyword);
    } catch (err: unknown) {
      reportError(setError, apiErrorMessage(err, "승인 철회에 실패했습니다."));
    } finally {
      setApproving(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (editId == null) return;
    try {
      setPdfDownloading(true);
      const name = sheetNo?.trim() || String(editId);
      await downloadSafetyCheckSheetPdf(editId, `안전점검표_${name}.pdf`);
    } catch (err) {
      notifyError(
        err instanceof Error
          ? err.message
          : "PDF 다운로드에 실패했습니다. Gotenberg 실행 여부를 확인해 주세요.",
      );
    } finally {
      setPdfDownloading(false);
    }
  };

  const handleUploadNow = async (files: FileList | null) => {
    if (!files?.length || attachmentsLocked) return;
    const selected = Array.from(files);
    if (fileRef.current) fileRef.current.value = "";
    if (editId == null) {
      setPendingFiles((prev) => [...prev, ...selected]);
      return;
    }
    try {
      setSaving(true);
      const uploaded = await uploadSafetyCheckSheetAttachments(
        editId,
        selected,
      );
      setAttachments((prev) => [...prev, ...uploaded]);
    } catch (err: unknown) {
      reportError(
        setError,
        apiErrorMessage(err, "첨부파일 업로드에 실패했습니다."),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAttachment = async (
    attachment: SafetyCheckSheetAttachment,
  ) => {
    if (editId == null || attachmentsLocked) return;
    if (!window.confirm(`"${attachment.fileName}" 첨부를 삭제할까요?`)) return;
    try {
      setSaving(true);
      await deleteSafetyCheckSheetAttachment(editId, attachment.attachmentId);
      setAttachments((prev) =>
        prev.filter((a) => a.attachmentId !== attachment.attachmentId),
      );
    } catch (err: unknown) {
      reportError(
        setError,
        apiErrorMessage(err, "첨부파일 삭제에 실패했습니다."),
      );
    } finally {
      setSaving(false);
    }
  };

  const renderCheckItems = (editable: boolean) => (
    <div className="ca-items">
      {items.map((item, idx) => (
        <div className="ca-item-card" key={item.id}>
          <div className="ca-item-card__head">
            <strong>항목 {idx + 1}</strong>
            {editable ? (
              <button
                type="button"
                className="ncr-btn is-danger"
                onClick={() =>
                  setItems((prev) => prev.filter((_, i) => i !== idx))
                }
              >
                삭제
              </button>
            ) : null}
          </div>
          <div className="ca-item-card__grid">
            <label className="is-wide">
              점검항목
              <textarea
                rows={2}
                value={item.checkItem}
                onChange={(e) => patchItem(idx, { checkItem: e.target.value })}
                disabled={!editable}
              />
            </label>
            <label>
              방법
              <textarea
                rows={2}
                value={item.method}
                onChange={(e) => patchItem(idx, { method: e.target.value })}
                disabled={!editable}
              />
            </label>
            <label>
              결과
              <select
                value={item.result}
                onChange={(e) => patchItem(idx, { result: e.target.value })}
                disabled={!editable}
              >
                <option value="">-</option>
                <option value="OK">양호</option>
                <option value="NG">미흡</option>
                <option value="NA">해당없음</option>
              </select>
            </label>
            <label className="is-full">
              발견사항
              <textarea
                rows={2}
                value={item.finding}
                onChange={(e) => patchItem(idx, { finding: e.target.value })}
                placeholder="미흡 시 내용"
                disabled={!editable}
              />
            </label>
            <label className="is-full">
              비고
              <textarea
                rows={2}
                value={item.remark}
                onChange={(e) => patchItem(idx, { remark: e.target.value })}
                disabled={!editable}
              />
            </label>
          </div>
        </div>
      ))}
      {items.length === 0 ? (
        <p className="quality-form__hint">등록된 점검 항목이 없습니다.</p>
      ) : null}
    </div>
  );

  const renderDetailActions = () => (
    <div
      className="ca-toolbar-actions"
      style={{ display: "flex", gap: 8, flexWrap: "wrap" }}
    >
      {isApproved(status) ? (
        <button
          type="button"
          className="ncr-btn is-primary"
          disabled={pdfDownloading}
          onClick={() => void handleDownloadPdf()}
        >
          {pdfDownloading ? "PDF 생성 중…" : "PDF 다운로드"}
        </button>
      ) : null}
      {canRevoke ? (
        <button
          type="button"
          className="ncr-btn is-danger"
          disabled={saving || approving}
          onClick={() => void handleRevoke()}
        >
          {approving ? "철회 중..." : "승인 철회"}
        </button>
      ) : null}
      {isDraft(status) && !isApprovalInProgress(approvalPending) ? (
        <>
          <button
            type="button"
            className="ncr-btn is-secondary"
            onClick={openEditFromDetail}
          >
            수정
          </button>
          <button
            type="button"
            className="ncr-btn is-primary"
            disabled={saving || approving}
            onClick={() => {
              setApprovalMessage("");
              setApprovalOpen(true);
            }}
          >
            결재 요청
          </button>
          {canApproveDirect ? (
            <button
              type="button"
              className="ncr-btn is-approve"
              disabled={saving || approving}
              onClick={() => void handleApprove()}
            >
              {approving ? "승인 중..." : "승인"}
            </button>
          ) : null}
          <button
            type="button"
            className="ncr-btn is-danger"
            disabled={saving || approving}
            onClick={() => {
              if (editId == null) return;
              if (
                !window.confirm(
                  `"${title || sheetNo || "점검시트"}"를 삭제할까요?`,
                )
              ) {
                return;
              }
              void deleteSafetyCheckSheet(editId).then(() => {
                backToList();
              });
            }}
          >
            삭제
          </button>
        </>
      ) : null}
      {isApprovalInProgress(approvalPending) ? (
        <ApprovalCancelButton
          detail={approvalPending}
          className="ncr-btn is-ghost"
          disabled={saving || approving}
          onCancelled={async () => {
            if (editId == null) return;
            const detail = await fetchSafetyCheckSheet(editId);
            applyDetail(detail);
            void loadList(searchKeyword);
          }}
        />
      ) : null}
      <button
        type="button"
        className="ncr-btn is-secondary"
        disabled={saving || approving}
        onClick={backToList}
      >
        목록
      </button>
    </div>
  );

  const renderEditActions = () => (
    <div className="quality-form__actions">
      <button
        type="submit"
        className="ncr-btn is-primary"
        disabled={saving || approving}
      >
        {saving ? "저장 중..." : "저장"}
      </button>
      <button
        type="button"
        className="ncr-btn is-secondary"
        disabled={saving || approving}
        onClick={() =>
          view.type === "edit" && editId != null
            ? setView({ type: "detail", sheetId: editId })
            : backToList()
        }
      >
        {view.type === "edit" ? "상세" : "목록"}
      </button>
    </div>
  );

  if (showForm) {
    return (
      <main className="quality-shell sg-panel">
        <form
          className="quality-form"
          onSubmit={
            isEditing ? (e) => void handleSave(e) : (e) => e.preventDefault()
          }
        >
          {isDetail ? (
            <div
              className="quality-shell__toolbar"
              style={{ marginBottom: 12 }}
            >
              <div>
                <h3 style={{ margin: 0 }}>안전관리 점검시트 상세</h3>
                <p className="quality-form__hint" style={{ margin: "4px 0 0" }}>
                  {sheetNo || "-"} · {title || "-"} ·{" "}
                  {withApprovalPendingLabel(
                    safetyStatusLabel(status),
                    approvalPending.approvalInProgress,
                  )}
                </p>
              </div>
              {renderDetailActions()}
            </div>
          ) : (
            renderEditActions()
          )}

          {error ? <p className="quality-error">{error}</p> : null}
          {approvalMessage ? (
            <p className="quality-form__hint">{approvalMessage}</p>
          ) : null}

          <section className="quality-form__section">
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: 16,
                alignItems: "flex-start",
                marginBottom: 12,
              }}
            >
              <div>
                <h3 style={{ margin: 0 }}>
                  {view.type === "create"
                    ? "안전관리 점검시트 등록"
                    : view.type === "edit"
                      ? "안전관리 점검시트 수정"
                      : "안전관리 점검시트"}
                </h3>
                {isEditing ? (
                  <p
                    className="quality-form__hint"
                    style={{ margin: "4px 0 0" }}
                  >
                    저장하면 상세보기로 이동합니다.
                  </p>
                ) : null}
              </div>
              <StampBox
                authorName={authorName}
                reviewerName={reviewerName}
                approverName={approverName}
              />
            </div>
            <div className="quality-form__grid">
              <label>
                시트번호
                <input
                  value={
                    sheetNo ||
                    (view.type === "create" ? "저장 시 자동 채번" : "-")
                  }
                  readOnly
                />
              </label>
              <label>
                점검일
                <input
                  type="date"
                  value={checkDate}
                  onChange={(e) => setCheckDate(e.target.value)}
                  readOnly={locked}
                />
              </label>
              <label className="is-span-2">
                제목 *
                <input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="점검시트 제목"
                  maxLength={255}
                  required
                  readOnly={locked}
                />
              </label>
              <label>
                점검 구역
                <input
                  value={areaName}
                  onChange={(e) => setAreaName(e.target.value)}
                  placeholder="예: 1공장 조립라인"
                  maxLength={120}
                  readOnly={locked}
                />
              </label>
              <label>
                점검자
                <input
                  value={checkerName}
                  onChange={(e) => setCheckerName(e.target.value)}
                  placeholder="점검자 이름"
                  maxLength={120}
                  readOnly={locked}
                />
              </label>
              <label className="is-span-2">
                비고
                <textarea
                  rows={3}
                  value={remark}
                  onChange={(e) => setRemark(e.target.value)}
                  placeholder="메모"
                  maxLength={2000}
                  readOnly={locked}
                />
              </label>
            </div>
          </section>

          <section className="quality-form__section">
            <div className="ca-section-head">
              <h3>점검 항목</h3>
              {isEditing ? (
                <button
                  type="button"
                  className="ncr-btn is-secondary"
                  onClick={() =>
                    setItems((prev) => [...prev, createEmptySafetyCheckItem()])
                  }
                >
                  항목 추가
                </button>
              ) : null}
            </div>
            {renderCheckItems(isEditing)}
          </section>

          <section className="quality-form__section">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                marginBottom: 8,
              }}
            >
              <h3 style={{ margin: 0 }}>첨부파일</h3>
              {!attachmentsLocked ? (
                <button
                  type="button"
                  className="ncr-btn is-secondary"
                  disabled={saving || approving}
                  onClick={() => fileRef.current?.click()}
                >
                  파일 추가
                </button>
              ) : null}
              <input
                ref={fileRef}
                type="file"
                multiple
                hidden
                onChange={(e) => void handleUploadNow(e.target.files)}
              />
            </div>
            <p className="quality-form__hint">
              관련 파일을 첨부할 수 있습니다. (최대 20개, 각 20MB)
              {editId == null ? " 신규는 저장 시 함께 업로드됩니다." : ""}
            </p>
            <ul className="edu-plan-files">
              {attachments.map((file) => (
                <li key={file.attachmentId}>
                  <div>
                    <strong>{file.fileName}</strong>
                    <span>{formatSize(file.fileSize)}</span>
                  </div>
                  <div className="edu-plan-files__actions">
                    <button
                      type="button"
                      className="ncr-btn is-secondary"
                      onClick={() =>
                        void downloadSafetyCheckSheetAttachment(
                          editId!,
                          file.attachmentId,
                          file.fileName,
                        ).catch(() =>
                          reportError(
                            setError,
                            "첨부파일 다운로드에 실패했습니다.",
                          ),
                        )
                      }
                    >
                      다운로드
                    </button>
                    {!attachmentsLocked ? (
                      <button
                        type="button"
                        className="ncr-btn is-danger"
                        disabled={saving || approving}
                        onClick={() => void handleDeleteAttachment(file)}
                      >
                        삭제
                      </button>
                    ) : null}
                  </div>
                </li>
              ))}
              {pendingFiles.map((file, idx) => (
                <li key={`pending-${file.name}-${idx}`}>
                  <div>
                    <strong>{file.name}</strong>
                    <span>저장 대기 · {formatSize(file.size)}</span>
                  </div>
                  <button
                    type="button"
                    className="ncr-btn is-danger"
                    onClick={() =>
                      setPendingFiles((prev) =>
                        prev.filter((_, i) => i !== idx),
                      )
                    }
                  >
                    제거
                  </button>
                </li>
              ))}
              {attachments.length === 0 && pendingFiles.length === 0 ? (
                <li className="is-empty">등록된 첨부파일이 없습니다.</li>
              ) : null}
            </ul>
          </section>

          {isDetail ? (
            <div className="quality-form__actions">{renderDetailActions()}</div>
          ) : (
            renderEditActions()
          )}

          {editId != null ? (
            <ApprovalRequestModal
              open={approvalOpen}
              titleDefault={`${title || "안전관리 점검시트"} 결재`}
              targetType={APPROVAL_TARGET_SAFETY_CHECK}
              targetId={editId}
              onClose={() => setApprovalOpen(false)}
              onSubmitted={() => {
                setApprovalOpen(false);
                setApprovalMessage(
                  "결재 요청이 등록되었습니다. 헤더의 결재 탭에서 확인할 수 있습니다.",
                );
                if (editId != null) {
                  void fetchSafetyCheckSheet(editId).then(applyDetail);
                }
                void loadList(searchKeyword);
              }}
            />
          ) : null}
        </form>
      </main>
    );
  }

  return (
    <main className="quality-shell sg-panel">
      <div className="quality-shell__toolbar">
        <div>
          <h2>
            {contractId != null
              ? "계약 안전관리 점검시트"
              : "안전관리 점검시트"}
          </h2>
          <p>안전관리 점검 결과를 기록·결재합니다.</p>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <input
            type="search"
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
            placeholder="번호·제목·구역·점검자 검색"
            style={{
              height: 34,
              padding: "0 12px",
              border: "1px solid #cbd5e1",
              borderRadius: 8,
              minWidth: 200,
              fontSize: 13,
            }}
          />
          <button
            type="button"
            className="ncr-btn is-primary"
            onClick={openCreate}
          >
            등록
          </button>
        </div>
      </div>

      {error ? <p className="quality-error">{error}</p> : null}

      <div className="quality-table-wrap">
        <table className="quality-table">
          <thead>
            <tr>
              <th style={{ width: 140 }}>시트번호</th>
              <th>제목</th>
              <th style={{ width: 110 }}>점검일</th>
              <th style={{ width: 120 }}>구역</th>
              <th style={{ width: 100 }}>점검자</th>
              <th style={{ width: 72 }}>첨부</th>
              <th style={{ width: 88 }}>상태</th>
              <th style={{ width: 80 }}>관리</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={8}>불러오는 중…</td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={8}>등록된 안전관리 점검시트가 없습니다.</td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr
                  key={row.sheetId}
                  onClick={() => void openDetail(row)}
                  style={{ cursor: "pointer" }}
                >
                  <td>{row.sheetNo || "-"}</td>
                  <td>{row.title || "-"}</td>
                  <td>{formatDate(row.checkDate)}</td>
                  <td>{row.areaName || "-"}</td>
                  <td>{row.checkerName || "-"}</td>
                  <td>{row.attachmentCount ?? 0}</td>
                  <td>
                    <span
                      className={`quality-badge is-${withApprovalPendingClass(
                        isApproved(row.status) ? "approved" : "draft",
                        row.approvalInProgress,
                      )}`}
                    >
                      {withApprovalPendingLabel(
                        safetyStatusLabel(row.status),
                        row.approvalInProgress,
                      )}
                    </span>
                  </td>
                  <td>
                    {!isApproved(row.status) && !isApprovalInProgress(row) ? (
                      <button
                        type="button"
                        className="ncr-btn is-danger"
                        onClick={(e) => void handleDeleteFromList(row, e)}
                      >
                        삭제
                      </button>
                    ) : (
                      "-"
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </main>
  );
}

export default SafetyCheckSheetPanel;
